<template>
  <input
    :id="id"
    ref="fileInputField"
    class="button-upload"
    :class="{ 'button-upload--fullwidth': fullWidth }"
    type="file"
    :accept="accept"
    @change="processFiles"
  />
</template>

<script setup lang="ts">
import { ref } from "vue";
import { v4 as uuid } from "uuid";

const props = withDefaults(defineProps<{
  id?: string;
  accept?: string;
  resetAfterSelect?: boolean;
  fullWidth?: boolean;
}>(), {
  id: `input-${uuid()}`,
  accept: "",
  resetAfterSelect: false,
  fullWidth: false
});

const emit = defineEmits<{
  (e: "input", files: File[]): void;
  (e: "select-file", file: File | null): void;
}>();

const files = ref<File[]>([]);
const fileInputField = ref<HTMLInputElement>();

function overrideFiles(files: File[] | null) {
  if (!fileInputField.value) return;
  if (files == null) {
    fileInputField.value.files = null;
    return;
  }

  const list = new DataTransfer();
  for (const f of files) {
    list.items.add(f);
  }

  const fileList = list.files;
  fileInputField.value.files = fileList;
}

function processFiles() {
  const field = fileInputField.value;
  if (field == null) {
    console.warn("No input.");
    return;
  }
  if (field.files == null) {
    console.warn("No files");
    return;
  }
  const newFiles = Array.from(field.files);
  files.value = newFiles;
  emit("input", newFiles);

  if (newFiles.length > 0) {
    emit("select-file", newFiles[0]);
  } else {
    emit("select-file", null);
  }

  // reset the file field:
  if (props.resetAfterSelect) {
    field.value = "";
  }
}

defineExpose({
  overrideFiles
});
</script>

<style lang="scss">
.button-upload {
  position: relative;
  display: block;
  width: 100%;
  min-height: calc(1em + 22px);
  overflow: hidden;
  border-top-left-radius: 1.25rem;
  border-bottom-left-radius: 1.25rem;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  border: 1px solid var(--color-border);
  border-left: none;
  transition: box-shadow 125ms, background-color 125ms, transform 125ms;

  &::file-selector-button {
    border-radius: inherit;
    background-color: var(--color-background-medium);
    color: var(--color-action);

    padding: 0.5em 2em;
    border: none;
    margin-right: 1.5rem;

    font-family: var(--font-family-ui);
    text-transform: uppercase;
    font-weight: 700;
    font-size: var(--font-size-button);
    letter-spacing: 0.02;
    line-height: 22px;
    text-align: center;

    transition: box-shadow 125ms, background-color 125ms, transform 125ms;

    &:active {
      box-shadow: inset 0 0.1em 0.25em rgba(0, 0, 0, 0.15);
      outline: 1px solid var(--color-action);
      outline-offset: -1px;
    }
  }
  &:hover {
    box-shadow: 0 0.0625em 0.125em var(--color-shadow-3);
  }
  &:hover::file-selector-button {
    background-color: var(--color-background-medium-hover);
  }

  &--fullwidth {
    border-radius: 1.25rem;
    border: none;
    padding: 0.5em;
  }
  &--fullwidth::file-selector-button {
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    margin-right: 0;
  }
}
</style>
